import { apiClient } from './client';
import type { Shortage, ShortageType, AcceptDeliveryResponse } from '@/types/models';

interface PaginatedShortagesResponse {
  data: Shortage[];
  count: number;
  page: number;
  limit: number;
}

interface ItemConfirmation {
  order_item_id: number;
  quantity_received: number;
}

export const warehouseApi = {
  acceptDelivery: async (deliveryId: number, items: ItemConfirmation[]): Promise<AcceptDeliveryResponse> => {
    const response = await apiClient.post<AcceptDeliveryResponse>(
      `/v1/warehouse/receiving/${deliveryId}/accept`,
      { items },
    );
    return response.data;
  },

  acceptAllDelivery: async (deliveryId: number): Promise<AcceptDeliveryResponse> => {
    const response = await apiClient.post<AcceptDeliveryResponse>(
      `/v1/warehouse/receiving/${deliveryId}/accept-all`,
    );
    return response.data;
  },

  reportShortage: async (data: {
    order_id: number;
    order_item_id: number;
    consolidated_delivery_id: number;
    shortage_type: ShortageType;
    description?: string;
  }): Promise<Shortage> => {
    const response = await apiClient.post<Shortage>('/v1/warehouse/shortages', data);
    return response.data;
  },

  resolveShortage: async (shortageId: number, description?: string): Promise<Shortage> => {
    const response = await apiClient.patch<Shortage>(
      `/v1/warehouse/shortages/${shortageId}/resolve`,
      { description },
    );
    return response.data;
  },

  listShortages: async (filters?: {
    delivery_id?: number;
    resolved?: boolean;
    page?: number;
    limit?: number;
  }): Promise<PaginatedShortagesResponse> => {
    const response = await apiClient.get<PaginatedShortagesResponse>('/v1/warehouse/shortages', {
      params: filters,
    });
    return response.data;
  },

  downloadDeliveryPdf: async (deliveryId: number): Promise<Blob> => {
    const response = await apiClient.get(`/v1/warehouse/receiving/${deliveryId}/print-pdf`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  downloadFormattedConsolidation: async (deliveryId: number): Promise<Blob> => {
    const response = await apiClient.get(`/v1/warehouse/receiving/${deliveryId}/formatted-xlsx`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  acceptAllBatch: async (batchId: number): Promise<{ batch_id: number; deliveries: Array<{ delivery_id: number; group_id: string; accepted_count: number }> }> => {
    const response = await apiClient.post(`/v1/warehouse/receiving/batch/${batchId}/accept-all`);
    return response.data;
  },

  deleteDelivery: async (deliveryId: number): Promise<{ deleted: boolean; group_id: string }> => {
    const response = await apiClient.delete<{ deleted: boolean; group_id: string }>(
      `/v1/warehouse/receiving/${deliveryId}`,
    );
    return response.data;
  },
};
